import React from 'react';
import type { FeatureFlag, FeatureFlagSortKey, SortDirection } from '../../../models/featureFlag';
import {
  describeFeatureFlagEffectiveStatus,
  getFeatureFlagEffectiveStatus,
  type FeatureFlagEffectiveStatus,
} from '../../../services/admin/featureFlagService';

const EFFECTIVE_STATUS_CLASS: Record<FeatureFlagEffectiveStatus, string> = {
  global: 'bo-status-active',
  targeted: 'bo-status-targeted',
  inactive: 'bo-status-inactive',
};

export interface FeatureFlagTableProps {
  /** Rows to render, already sorted and paginated by the parent. */
  flags: FeatureFlag[];
  sortKey: FeatureFlagSortKey;
  sortDirection: SortDirection;
  onSort: (key: FeatureFlagSortKey) => void;
  onEdit: (flag: FeatureFlag) => void;
  onToggleEnabled: (flag: FeatureFlag) => void;
  onToggleArchived: (flag: FeatureFlag) => void;
  onManageAssignments: (flag: FeatureFlag) => void;
  onViewAudit: (flag: FeatureFlag) => void;
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

function sortIndicator(active: boolean, direction: SortDirection): string {
  if (!active) {
    return '';
  }
  return direction === 'asc' ? ' ▲' : ' ▼';
}

export function FeatureFlagTable({
  flags,
  sortKey,
  sortDirection,
  onSort,
  onEdit,
  onToggleEnabled,
  onToggleArchived,
  onManageAssignments,
  onViewAudit,
}: FeatureFlagTableProps): React.ReactElement {
  return (
    <div className="bo-card">
      <div className="bo-admin-table-head">
        <h2 className="bo-admin-section-title">Feature flags</h2>
        <p className="bo-muted">Control platform functionality without a deployment.</p>
      </div>
      <div className="bo-admin-table-wrap">
        <table className="bo-admin-table">
          <thead>
            <tr>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('key')}>
                  Key{sortIndicator(sortKey === 'key', sortDirection)}
                </button>
              </th>
              <th>Description</th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('enabledGlobally')}>
                  Enabled globally{sortIndicator(sortKey === 'enabledGlobally', sortDirection)}
                </button>
              </th>
              <th>Assignments</th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('createdAt')}>
                  Created{sortIndicator(sortKey === 'createdAt', sortDirection)}
                </button>
              </th>
              <th>
                <button type="button" className="bo-link-button" onClick={() => onSort('isArchived')}>
                  Status{sortIndicator(sortKey === 'isArchived', sortDirection)}
                </button>
              </th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {flags.map((flag) => {
              const totalAssigned = flag.assignedUserCount + flag.assignedCompanyCount + flag.assignedRoleCount;
              const effectiveStatus = getFeatureFlagEffectiveStatus(flag.isArchived, flag.enabledGlobally, totalAssigned);
              return (
              <tr key={flag.id}>
                <td>
                  <strong>{flag.key}</strong>
                </td>
                <td>{flag.description || <span className="bo-muted">—</span>}</td>
                <td>
                  <span className={`bo-status-pill ${flag.enabledGlobally ? 'bo-status-active' : 'bo-status-inactive'}`}>
                    {flag.enabledGlobally ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td>
                  <div>
                    <span className={`bo-status-pill ${EFFECTIVE_STATUS_CLASS[effectiveStatus]}`}>
                      {describeFeatureFlagEffectiveStatus(effectiveStatus, totalAssigned)}
                    </span>
                  </div>
                  <button type="button" className="bo-link-button" onClick={() => onManageAssignments(flag)}>
                    {flag.assignedUserCount} users · {flag.assignedCompanyCount} companies · {flag.assignedRoleCount} roles
                  </button>
                </td>
                <td>
                  <div>{formatDate(flag.createdAt)}</div>
                  <span className="bo-muted">{flag.createdBy || 'Unknown'}</span>
                </td>
                <td>
                  <span className={`bo-status-pill ${flag.isArchived ? 'bo-status-inactive' : 'bo-status-active'}`}>
                    {flag.isArchived ? 'Archived' : 'Active'}
                  </span>
                </td>
                <td>
                  <div className="bo-actions">
                    <button
                      type="button"
                      className="bo-btn bo-btn-secondary"
                      onClick={() => onEdit(flag)}
                      disabled={flag.isArchived}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="bo-btn bo-btn-secondary"
                      onClick={() => onToggleEnabled(flag)}
                      disabled={flag.isArchived}
                    >
                      {flag.enabledGlobally ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="bo-btn bo-btn-danger"
                      onClick={() => onToggleArchived(flag)}
                    >
                      {flag.isArchived ? 'Unarchive' : 'Archive'}
                    </button>
                    <button type="button" className="bo-btn bo-btn-secondary" onClick={() => onViewAudit(flag)}>
                      Audit history
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
